import { ValidationIssue, ErrorResult } from '../../models/error-result.model';

function getPayload(err: unknown): Record<string, unknown> {
  const anyErr = err as Record<string, unknown>;
  return (anyErr?.error ?? anyErr) as Record<string, unknown>;
}

function extractValidationIssues(payload: Record<string, unknown>): ValidationIssue[] {
  if (!payload || typeof payload !== 'object') return [];
  if (!('isValid' in payload && 'issues' in payload && Array.isArray(payload.issues))) return [];
  if (payload.isValid !== false) return [];

  return payload.issues
    .map(i => ({
      property: String((i as any)?.property ?? '').trim(),
      message: String((i as any)?.message ?? '').trim(),
    }))
    .filter(i => i.property || i.message);
}

function extractMessage(payload: Record<string, unknown>): string | null {
  const message = (payload?.message ?? payload?.title) as string | undefined;
  if (!message?.trim()) return null;

  return message.trim();
}

function formatErrors(errors: Record<string, unknown>): string | null {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      const msgs = value.map(v => String(v ?? '').trim()).filter(Boolean);
      if (msgs.length) parts.push(`${key}: ${msgs.join(', ')}`);
    } else {
      const msg = String(value ?? '').trim();
      if (msg) parts.push(`${key}: ${msg}`);
    }
  }

  return parts.length ? parts.join(' • ') : null;
}

function formatMessage(err: unknown): string | null {
  const payload = getPayload(err);

  if (payload?.errors && typeof payload.errors === 'object') {
    const formatted = formatErrors(payload.errors as Record<string, unknown>);
    if (formatted) return formatted;
  }

  return extractMessage(payload);
}

export function processBackendError(err: unknown, fallback: string): ErrorResult {
  const payload = getPayload(err);
  const issues = extractValidationIssues(payload);

  return {
    message: issues.length ? extractMessage(payload) : (formatMessage(err) ?? fallback),
    issues,
  };
}
